import Phaser from "phaser";
import type { TextDirection } from "@yjsgames/i18n";
import { computeRowLayout, type RowLayoutItem } from "./mirror.js";

export interface DirectionalRowOptions {
  readonly dir: TextDirection;
  /** 아이템 사이 간격(px). default 0. */
  readonly gap?: number;
}

/**
 * 방향 인지 가로 레이아웃 컨테이너 — 자식을 **논리 순서**로 추가하면 LTR/RTL에 맞춰 자동 배치한다.
 * 좌표 계산은 `computeRowLayout`(순수)에 위임. 절대좌표 대신 이 컨테이너에 자식을 담으면 언어 방향만 바꿔
 * `setDirection`으로 전체 미러링이 끝난다.
 *
 * ⚠ 계약: 자식은 origin x=0(좌상단 기준)이어야 한다. 폭은 `displayWidth`(없으면 `width`)로 측정하므로
 *   자식의 표시 크기가 확정된 뒤 `addItems`를 호출할 것.
 */
export class DirectionalRow extends Phaser.GameObjects.Container {
  private dir: TextDirection;
  private readonly gap: number;
  private readonly rowItems: Phaser.GameObjects.GameObject[] = [];

  constructor(scene: Phaser.Scene, x: number, y: number, opts: DirectionalRowOptions) {
    super(scene, x, y);
    this.dir = opts.dir;
    this.gap = opts.gap ?? 0;
  }

  /** 자식을 논리 순서로 추가하고 즉시 재배치. */
  addItems(children: ReadonlyArray<Phaser.GameObjects.GameObject>): this {
    for (const c of children) {
      this.rowItems.push(c);
      this.add(c);
    }
    this.relayout();
    return this;
  }

  /** 방향 전환 — 같은 자식을 반대 방향으로 재배치. */
  setDirection(dir: TextDirection): this {
    this.dir = dir;
    this.relayout();
    return this;
  }

  private relayout(): void {
    const items: RowLayoutItem[] = this.rowItems.map((c) => ({ width: widthOf(c) }));
    const { xs } = computeRowLayout(items, this.gap, this.dir);
    this.rowItems.forEach((c, i) => {
      const obj = c as Phaser.GameObjects.GameObject & { x?: number };
      if (typeof obj.x === "number") obj.x = xs[i] ?? 0;
    });
  }
}

/** 자식 표시 폭 — displayWidth(스케일 반영) 우선, 없으면 width, 둘 다 없으면 0. */
function widthOf(c: Phaser.GameObjects.GameObject): number {
  const o = c as { displayWidth?: number; width?: number };
  return o.displayWidth ?? o.width ?? 0;
}
